import React from "react";
import {Target, Users, TrendingUp, Heart} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Mission Driven",
      description: "Our mission is to make Osrey accessible and understandable to everyone, from beginners to experts in the field of Osreys.",
    },
    {
      icon: Users,
      title: "Community First",
      description: "We believe in the power of community. Every piece of content about Osrey is created with our users in mind.",
    },
    {
      icon: TrendingUp,
      title: "Innovation Focus",
      description: "Staying ahead of trends in Osrey and Osreys technology to bring you the most relevant and cutting-edge information.",
    },
    {
      icon: Heart,
      title: "Passion for Excellence",
      description: "We're passionate about Osrey and dedicated to delivering high-quality, accurate, and engaging content about Osreys.",
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A0D12] via-[#1C2A35] to-[#0A0D12] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00D4AA] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#F5F7FA] mb-6">
            About Osrey
          </h1>
          <p className="text-xl text-[#F5F7FA]/80 leading-relaxed">
            Everything Osrey, Curated and Connected
          </p>
        </div>
      </section>

      {/* What is Osrey Section */}
      <section className="py-20 bg-[#1C2A35]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#F5F7FA] mb-8">
            What is Osrey?
          </h2>
          <div className="space-y-6 text-lg text-[#F5F7FA]/80 leading-relaxed">
            <p>
              Osrey represents the next generation of digital connectivity and information architecture. Born from the need to create more meaningful connections in an increasingly complex digital world, Osrey has evolved into a comprehensive ecosystem that empowers individuals and organizations to harness the power of interconnected knowledge.
            </p>
            <p>
              At its core, Osrey is about making complex information accessible, actionable, and engaging. Through innovative approaches to data organization and community-driven insights, Osrey transforms how we interact with information and each other. The platform serves as a bridge between traditional knowledge systems and modern digital needs.
            </p>
            <p>
              Understanding Osrey means recognizing its potential to revolutionize how we learn, share, and grow together. Whether you're exploring Osrey for personal development or professional advancement, the principles of Osrey and Osreys provide a solid foundation for success in today's interconnected world.
            </p>
          </div>
        </div>
      </section>

      {/* The Meaning Behind Osreys */}
      <section className="py-20 bg-[#0A0D12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#F5F7FA] mb-8">
            The Meaning Behind Osreys
          </h2>
          <div className="space-y-6 text-lg text-[#F5F7FA]/80 leading-relaxed">
            <p>
              Osreys, the plural form that encompasses the broader ecosystem, represents the collective power of interconnected Osrey instances working in harmony. Think of Osreys as individual nodes in a vast network, each contributing unique value while maintaining connection to the whole.
            </p>
            <p>
              The concept of Osreys emerged from observing how individual pieces of information gain exponential value when properly connected and contextualized. Each Osrey within the Osreys framework serves a specific purpose, yet together they create something far greater than the sum of their parts.
            </p>
            <p>
              Understanding Osreys is crucial for anyone looking to maximize the potential of Osrey. The relationship between Osrey and Osreys mirrors the relationship between individual learning and collective wisdom – both essential, both powerful, and most effective when working together.
            </p>
          </div>
        </div>
      </section>

      {/* Vision and Values */}
      <section className="py-20 bg-[#1C2A35]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#F5F7FA] mb-4">
              Our Vision for the Future
            </h2>
            <p className="text-lg text-[#F5F7FA]/70 max-w-3xl mx-auto">
              We envision a world where Osrey and Osreys become the standard for how people interact with information, enabling unprecedented levels of collaboration, learning, and innovation across all sectors of society.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="p-8 bg-[#0A0D12] rounded-xl border border-[#3A4E5F] hover:border-[#00D4AA] transition-all duration-300 hover:transform hover:-translate-y-2"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#00D4AA] to-[#FF6B35] rounded-lg flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-[#F5F7FA] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#F5F7FA]/70 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Driven Concept */}
      <section className="py-20 bg-[#0A0D12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#F5F7FA] mb-8">
            Community Driven Innovation
          </h2>
          <div className="space-y-6 text-lg text-[#F5F7FA]/80 leading-relaxed">
            <p>
              The Osrey community is the heartbeat of everything we do. Every article, guide, and resource about Osrey and Osreys is shaped by feedback, questions, and insights from our vibrant community of users, developers, and enthusiasts.
            </p>
            <p>
              We believe that the best innovations in Osrey come from collaborative effort. That's why we've built our platform to encourage contribution, discussion, and shared learning. Whether you're sharing your experience with Osreys or asking questions about Osrey fundamentals, your voice matters.
            </p>
            <p>
              Join thousands of Osrey enthusiasts who are shaping the future of how we understand and implement Osreys in real-world applications. Together, we're building more than just a knowledge base – we're creating a movement that will define the next era of digital connectivity and information sharing.
            </p>
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-[#1C2A35] to-[#3A4E5F] rounded-xl border border-[#00D4AA]/30">
            <h3 className="text-2xl font-bold text-[#F5F7FA] mb-4">
              Join the Osrey Community
            </h3>
            <p className="text-[#F5F7FA]/80 mb-6">
              Be part of the growing community exploring the possibilities of Osrey and Osreys. Share your insights, learn from others, and help shape the future of this innovative platform.
            </p>
            <button className="px-8 py-4 bg-gradient-to-r from-[#00D4AA] to-[#FF6B35] text-white font-semibold rounded-lg hover:shadow-2xl hover:shadow-[#00D4AA]/30 transition-all duration-300 hover:scale-105">
              Get Started Today
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;